import { useMemo, useState } from 'react';
import { useCurrentPatient } from '@/hooks/useCurrentPatient';
import {
  usePatientFormsAssignments,
  useRequestPortalFormLink,
  type PatientFormsAssignment,
} from '@/hooks/usePatientFormsAssignments';
import { publicUrlFromAssignmentMetadata } from '@/lib/formsUrls';
import { useQuestionnaireNameMap } from '@/hooks/useQuestionnaireNameMap';
import { assignmentQuestionnaireName } from '@/lib/questionnaireDisplayName';
import { PortalPageHead } from '@/components/portal/PortalChrome';
import { PortalIcon } from '@/components/portal/PortalIcon';
import { formatPortalDateTime } from '@/lib/portalFormat';

export function PortalQuestionnaires() {
  const { data: patient, isLoading: patientLoading } = useCurrentPatient();
  const {
    data: assignments,
    isLoading,
    error,
  } = usePatientFormsAssignments(patient?.id);
  const clinicId = patient?.clinic_id ?? assignments?.[0]?.clinic_id ?? null;
  const { map } = useQuestionnaireNameMap(clinicId, 'da');

  const { active, completed, terminal } = useMemo(() => {
    if (!assignments) return { active: [], completed: [], terminal: [] };
    return {
      active: assignments.filter((row) => row.status === 'sent' || row.status === 'reminded'),
      completed: assignments.filter((row) => row.status === 'completed'),
      terminal: assignments.filter((row) => row.status === 'expired' || row.status === 'cancelled'),
    };
  }, [assignments]);

  if (patientLoading || isLoading) {
    return (
      <>
        <PortalPageHead title="Spørgeskemaer" subtitle="Skemaer, som klinikken har sendt til dig." />
        <section className="card">
          <p className="help">Henter spørgeskemaer…</p>
        </section>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PortalPageHead title="Spørgeskemaer" subtitle="Skemaer, som klinikken har sendt til dig." />
        <section className="card">
          <p className="help">Spørgeskemaerne kunne ikke hentes. Prøv igen.</p>
        </section>
      </>
    );
  }

  const empty = active.length === 0 && completed.length === 0 && terminal.length === 0;

  return (
    <>
      <PortalPageHead
        title="Spørgeskemaer"
        subtitle="Skemaer, som klinikken har sendt til dig. Dine svar går direkte til din behandler."
      />
      {empty ? (
        <section className="card">
          <p className="help">Du har ingen spørgeskemaer endnu.</p>
        </section>
      ) : (
        <>
          <AssignmentSection title="Afventer svar" items={active} names={map} />
          <AssignmentSection title="Udfyldt" items={completed} names={map} spaced />
          <AssignmentSection title="Udløbne" items={terminal} names={map} spaced />
        </>
      )}
    </>
  );
}

function AssignmentSection({
  title,
  items,
  names,
  spaced,
}: {
  title: string;
  items: PatientFormsAssignment[];
  names: ReturnType<typeof useQuestionnaireNameMap>['map'];
  spaced?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <section className="card" style={spaced ? { marginTop: 24 } : undefined}>
      <div className="card-h">
        <h2>{title}</h2>
        <span className="count">{items.length}</span>
      </div>
      <div style={{ marginTop: 4 }}>
        {items.map((assignment) => (
          <AssignmentRow
            key={assignment.id}
            assignment={assignment}
            name={assignmentQuestionnaireName(assignment, names, 'da', 'Spørgeskema')}
          />
        ))}
      </div>
    </section>
  );
}

function AssignmentRow({
  assignment,
  name,
}: {
  assignment: PatientFormsAssignment;
  name: string;
}) {
  const [linkError, setLinkError] = useState<string | null>(null);
  const { mutate: requestLink, isPending } = useRequestPortalFormLink();
  const isActive = assignment.status === 'sent' || assignment.status === 'reminded';
  const cachedUrl = publicUrlFromAssignmentMetadata(assignment.metadata);

  const open = () => {
    if (cachedUrl) {
      window.open(cachedUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setLinkError(null);
    requestLink(assignment.id, {
      onSuccess(result) {
        if (result.ok && result.url) window.open(result.url, '_blank', 'noopener,noreferrer');
        else setLinkError('Linket er ikke klar endnu. Prøv igen om lidt.');
      },
      onError() {
        setLinkError('Spørgeskemaet kunne ikke åbnes.');
      },
    });
  };

  const chip =
    assignment.status === 'completed'
      ? { tone: 'green', label: 'Udfyldt', icon: 'check' as const }
      : assignment.status === 'expired' || assignment.status === 'cancelled'
        ? { tone: 'neutral', label: assignment.status === 'cancelled' ? 'Annulleret' : 'Udløbet', icon: 'x' as const }
        : { tone: 'amber', label: 'Afventer svar', icon: 'clock' as const };

  return (
    <div className="row">
      <span className={`ic-tile${isActive ? ' amber' : ''}`}>
        <PortalIcon name="clipboard" />
      </span>
      <div className="grow">
        <div className="t">{name}</div>
        <div className="s">
          {assignment.due_at && isActive
            ? `Svarfrist ${formatPortalDateTime(assignment.due_at)}`
            : assignment.completed_at
              ? `Udfyldt ${formatPortalDateTime(assignment.completed_at)}`
              : assignment.sent_at
                ? `Sendt ${formatPortalDateTime(assignment.sent_at)}`
                : ''}
        </div>
        {linkError ? <div className="s">{linkError}</div> : null}
      </div>
      <div className="r">
        <span className={`chip chip-${chip.tone}`}>
          <PortalIcon name={chip.icon} size="xs" />
          {chip.label}
        </span>
        {isActive ? (
          <button type="button" className="btn btn-primary" onClick={open} disabled={isPending}>
            Udfyld
          </button>
        ) : null}
      </div>
    </div>
  );
}
